// ============================================================
// PrintReport — print.config.js
//
// All print configuration lives here:
//   - Section-based variable overrides (logos, org names, etc.)
//   - Header and footer HTML templates
//   - Variable resolution logic
//
// HOW SECTION OVERRIDES WORK:
//   1. Add an entry to `sectionOverrides` with a `match` string
//      that corresponds to the URL path for that section.
//   2. The resolver checks window.location.pathname using
//      startsWith(), so '/analytics' will match '/analytics',
//      '/analytics/reports', '/analytics/report/2026', etc.
//   3. The first match in the list wins — put more specific
//      paths above more general ones.
//   4. Any variable not specified in the override falls back
//      to the value in `defaults`.
//
// HOW TO ADD A NEW VARIABLE:
//   1. Add it to `defaults` with a fallback value.
//   2. Optionally override it per section in `sectionOverrides`.
//   3. Reference it in the header/footer templates as {{variableName}}.
// ============================================================

// ------------------------------------------------------------
// Default variable values
// Used when no section override matches the current URL.
// ------------------------------------------------------------
const defaults = {
  logoUrl: "https://cra-sa.com/siteworkspace/logo.svg/@@images/image",
  orgName: "Centre for Risk Analysis",
  reportName: "",
  // Add new default variables here
};

// ------------------------------------------------------------
// Section overrides
// Matched against window.location.pathname using startsWith().
// First match wins — order matters for overlapping paths.
// Only specify the variables you want to override per section;
// everything else falls back to defaults above.
// ------------------------------------------------------------
const sectionOverrides = [
  {
    match: "/products/risk-alert",
    logoUrl: "https://cra-sa.com/siteworkspace/logo.svg/@@images/image",
    orgName: "Centre for Risk Analysis",
    reportName: "Risk Alert",
  },
  // {
  //   match: '/research',
  //   logoUrl: '/assets/logos/research-logo.svg',
  //   orgName: 'Research Unit',
  // },
];

// ------------------------------------------------------------
// Variable resolution
// Merges defaults with the first matching section override,
// then adds dynamic variables that are always resolved at
// print time (date, page title, URL).
// ------------------------------------------------------------
const resolveVariables = () => {
  // Find the first section whose match string is a prefix of the current path
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const matchedSection = sectionOverrides.find((section) =>
    pathname.startsWith(section.match),
  );

  // Merge: defaults → section override (if any)
  const resolved = { ...defaults, ...(matchedSection || {}) };

  // Remove the `match` key — it's config, not a template variable
  delete resolved.match;

  // Add dynamic variables resolved fresh at print time
  resolved.reportTitle = typeof document !== "undefined" ? document.title : "";
  resolved.printDate = new Date().toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  resolved.siteUrl =
    typeof window !== "undefined" ? window.location.origin : "";
  resolved.pageUrl = typeof window !== "undefined" ? window.location.href : "";

  // Effective date — read from the rendered .dates element on the page.
  // Prefers the machine-readable datetime attribute for consistent formatting;
  // falls back to visible text content if the attribute is absent.
  resolved.effectiveDate =
    typeof document !== "undefined"
      ? document.querySelector(".dates time")?.getAttribute("datetime")
        ? new Date(
            document.querySelector(".dates time").getAttribute("datetime"),
          ).toLocaleDateString("en-ZA", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : (document.querySelector(".dates time")?.textContent?.trim() ?? "")
      : "";

  return resolved;
};

// ------------------------------------------------------------
// Template resolver
// Replaces {{variableName}} tokens in an HTML string with
// their resolved values. Called at print time by View.jsx.
// ------------------------------------------------------------
export const resolveTemplate = (htmlString) => {
  const variables = resolveVariables();
  return Object.entries(variables).reduce((result, [key, value]) => {
    return result.replaceAll(`{{${key}}}`, value ?? "");
  }, htmlString);
};

// ------------------------------------------------------------
// Header template
// Edit the HTML below to change the print header layout.
// Use {{variableName}} for any variable defined above.
// ------------------------------------------------------------
export const headerTemplate = `
<div class="print-report-header">
  <div class="print-report-header__logo">
    <img src="{{logoUrl}}" alt="{{orgName}}" />
  </div>
  <div class="print-report-header__meta">
    <p class="print-report-header__title">{{reportName}}</p>
    <p class="print-report-header__date">{{effectiveDate}}</p>
  </div>
</div>
`;

// ------------------------------------------------------------
// Footer template
// Edit the HTML below to change the print footer layout.
// Use {{variableName}} for any variable defined above.
// ------------------------------------------------------------
export const footerTemplate = `
<div class="print-report-footer">
  <p>Report compiled by <span class="print-report-footer__org">{{orgName}}</span></p>
  <p class="print-report-header__date">{{effectiveDate}}</p>
</div>
`;
